import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react'
import posthog from 'posthog-js'
import { allowRebuild } from '../graph/recovery'
import GraphFailed from './GraphFailed'

interface Props {
  children: ReactNode
}

interface State {
  failed: boolean
  exhausted: boolean
  key: number
}

/**
 * The graph's error boundary (app#349).
 *
 * ⚠⚠ THE APP HAS NO OTHER ERROR BOUNDARY, so an error that escapes the graph's render or effects
 * unmounts the WHOLE page: measured in the harness, `#root` empty and no data change brings it
 * back. `graph/core.ts` already reports a failed draw as an event, which is the common path; this
 * is the second line, for everything else in the graph's subtree (its hooks, `useMergedData`, the
 * build effect).
 *
 * It mounts the graph again under a new key, inside the same budget `Graph.tsx` uses, and past the
 * budget it shows `GraphFailed` rather than trying for ever.
 */
export default class GraphBoundary extends Component<Props, State> {
  state: State = { failed: false, exhausted: false, key: 0 }
  private history: number[] = []
  private timer?: ReturnType<typeof setTimeout>

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('graph: render failed', error, info.componentStack)
    const next = allowRebuild(this.history, Date.now())
    this.history = next.history
    posthog.capture('graph rebuild', { reason: 'render failed', message: error.message, allowed: next.allowed })
    if (!next.allowed) {
      this.setState({ exhausted: true })
      return
    }
    // A short pause, so a fault in the render that follows at once is not immediately repeated.
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.setState(s => ({ failed: false, key: s.key + 1 })), 1000)
  }

  componentWillUnmount() {
    clearTimeout(this.timer)
  }

  private redraw = () => {
    this.history = []
    this.setState(s => ({ failed: false, exhausted: false, key: s.key + 1 }))
  }

  render() {
    if (this.state.exhausted) return <GraphFailed onRedraw={this.redraw} />
    if (this.state.failed) return null
    return <Fragment key={this.state.key}>{this.props.children}</Fragment>
  }
}
